import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, UserCircle2, Copy, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { copyToClipboard } from '../utils/clipboard';
import SEO from './SEO';

const ABOUT_SECTIONS = [
  { title: 'The Problem We Solve', paragraphs: [
    'Investors often manage multiple investments and assets across different banks, asset managers, investment platforms, and jurisdictions.',
    'This fragmentation creates two silent problems. Today, it makes it impossible to see all your investments in one place \u2014 no single view of total performance, allocation, or risk exposure. Tomorrow, if life takes an unexpected turn, it makes it just as impossible for the people you care about to find or make sense of what you built.',
    'MyInvestFolio was built to solve both problems \u2014 clarity for you today, continuity for the people you care about, over generations.',
  ]},
  { title: 'What MyInvestFolio Is', footerLinkTo: '/pricing-layers', footerLinkLabel: 'See exactly what\u2019s in each tier \u2192', paragraphs: [
    'MyInvestFolio is a private, independent portfolio infrastructure for discerning investors. It consolidates equities, fixed income, private equity, real estate, alternative assets, and digital currencies into one unified view: across institutions, currencies, holding entities, trusts, and foundations.',
    'MyInvestFolio brings it all together in a simple and structured way. The infrastructure supports both direct and indirect holdings. Your portfolio reflects how your wealth is actually structured, not a simplified version of it. Whether you own assets in your own name or through other structures, you can view them in one place, giving you a complete, secure view of your investment portfolio.',
    'This makes decision-making more confident and less stressful.',
    'The platform is offered in two connected tiers. Continuity System is the entry door \u2014 a private, encrypted record of what you own, who to contact, and where the paperwork lives, quietly protected by a check-in cycle for the people you care about. Portfolio Clarity sits on top and includes everything in Continuity System, plus the full analytical view \u2014 consolidated performance, allocation, tax data and reporting across every institution, entity and currency you hold.',
  ]},
  { title: 'Continuity System by Design', paragraphs: [
    "For most people, the biggest risk to their wealth isn\u2019t a market crash \u2014 it\u2019s the fact that nobody else knows how the pieces fit together. People you care about, unable to locate accounts when you\u2019re not available. A trusted person who has never met the private banker. Documents scattered across a lifetime \u2014 only you knowing where.",
    'Continuity System is our answer. A quiet, encrypted layer that turns your portfolio into a map the people you care about can follow \u2014 but only when it\u2019s truly needed. You choose your Trusted Contacts. You capture, in plain language, what each investment is and where the paperwork lives. You attach documents that stay locked to everyone (including us) unless our quiet check-in cycle confirms otherwise.',
    'Nothing sits on our servers in a form we ourselves can read. It\u2019s wealth continuity as an ongoing practice, not a one-time paperwork event.',
  ]},
  { title: 'Privacy and Security by Design', paragraphs: [
    "Privacy is at the core of MyInvestFolio. It\u2019s not an afterthought. Our architecture ensures that your data stays yours: encrypted, anonymized and private. Your data remains under your control at all times. Nothing is shared unless you choose to share it. Even MyInvestFolio\u2019s own team cannot view your portfolio. Access to user data is anonymized by default, unless you explicitly grant access.",
    'You can use the platform with confidence, knowing that your investment data stays secure and private.',
  ]},
  { title: 'Clarity Today. Continuity Over Generations.', paragraphs: [
    'MyInvestFolio was born from the belief that sophisticated investors deserve technology that matches their ambition \u2014 without compromising their privacy, their time, or their peace of mind.',
    "We help you see and manage your investment portfolio with confidence today. And we help you rest easy knowing that if the day ever comes when you cannot be there yourself, the people you care about will have the information and access they need \u2014 quietly, respectfully, and on your terms.",
  ]},
  { title: 'Joining MyInvestFolio — Two Ways In', paragraphs: [
    'Continuity System is open to investors who value a private record of what they own \u2014 you can sign yourself up in a few minutes. Portfolio Clarity remains by invitation only. We grow through private introductions, not public marketing, to keep a community of genuine investors who value privacy, control and a platform that respects the complexity of real wealth management.',
  ]},
  { title: 'Getting Started', paragraphs: [
    'You can start with Continuity System today \u2014 sign up yourself with just your name, email and password \u2014 or request an invitation to Portfolio Clarity for the full analytical view.',
    'Getting started is flexible. You can begin with a few assets and build over time. You can enter data manually, import from a file, or follow a guided setup. If you prefer, a short private virtual session is available to help you get started quickly.',
  ]},
];

// Per-card accent color. Privacy and exclusivity get blue (trust signal);
// everything else stays brand emerald.
const ACCENT_BY_TITLE = {
  'Continuity System by Design': 'blue',
  'Privacy and Security by Design': 'blue',
  'By Invitation Only': 'blue',
  'Joining MyInvestFolio — Two Ways In': 'blue',
};

const ContentSection = ({ title, paragraphs, footerLinkTo, footerLinkLabel }) => {
  const accent = ACCENT_BY_TITLE[title] || 'emerald';
  const titleColor = accent === 'blue' ? 'text-blue-400' : 'text-emerald-400';
  const barColor = accent === 'blue' ? 'bg-blue-500' : 'bg-emerald-500';
  return (
    <div className="relative bg-gray-800 rounded-xl p-8 border border-gray-700 overflow-hidden">
      <span aria-hidden className={`absolute left-0 top-0 bottom-0 w-1 ${barColor}`} />
      <h2 className={`text-xl font-semibold mb-3 ${titleColor}`}>{title}</h2>
      {paragraphs.map((text, i) => (
        <p key={`${title}-p-${i}`} className={`text-lg ${i < paragraphs.length - 1 ? 'mb-4' : ''}`}>{text}</p>
      ))}
      {footerLinkTo && (
        <Link
          to={footerLinkTo}
          className={`inline-block mt-4 text-sm font-semibold ${titleColor} hover:underline underline-offset-2`}
          data-testid={`about-section-link-${title.toLowerCase().replace(/[^a-z]+/g, '-')}`}
        >
          {footerLinkLabel}
        </Link>
      )}
    </div>
  );
};

const ONE_LINE = "MyInvestFolio is a personal system of wealth record for discerning investors \u2014 starting with Continuity System (your private record of what you own) and rising to Portfolio Clarity (the full analytical view), all encrypted end-to-end.";

const THREE_LINE = "MyInvestFolio is a private, independent system of wealth record for discerning investors. Continuity System \u2014 the entry tier \u2014 gives you an encrypted personal record of every asset you own, plus a quiet safety net so the people you care about can find and understand your investments if you're ever unable to explain them yourself. Portfolio Clarity sits on top and adds the full analytical view \u2014 consolidated performance, allocation, tax data and reporting across every institution, entity and currency. Continuity System is open to sign up; Portfolio Clarity is by private introduction only.";

const LINKEDIN_POST = `Posted 30 July 2026

MyInvestFolio \u2014 Clarity Today. Continuity Over Generations.

Most digital wealth tools were built for the moment of trade. Very few were built for the reality of holding a serious portfolio together \u2014 across banks, brokerages, trusts, currencies and years.

MyInvestFolio is a private, independent system of wealth record for discerning investors, in two connected tiers.

Continuity System \u2014 the entry door. An encrypted personal record of every asset you own, with a quiet safety net so the people you trust can find and understand what you built if the day ever comes when you cannot explain it yourself.

Portfolio Clarity \u2014 the full picture. Everything in Continuity System, plus consolidated performance, allocation, tax data and reporting across every institution, entity and currency you hold.

Continuity System is open to sign up. Portfolio Clarity is by private introduction only.

\u2192 myinvestfolio.io

#WealthManagement #PrivateWealth #PortfolioInfrastructure #WealthContinuity #FamilyOffice #EstatePlanning`;

const BoilerplateCard = () => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    const combined = `One line:\n${ONE_LINE}\n\nThree lines:\n${THREE_LINE}`;
    const ok = await copyToClipboard(combined);
    if (ok) {
      setCopied(true);
      toast.success('Reference description copied — ready to paste');
      setTimeout(() => setCopied(false), 2500);
    } else {
      toast.error('Could not copy automatically — please select the text and copy manually');
    }
  };
  return (
    <div className="relative bg-gray-900/60 rounded-xl p-8 border border-gray-700 overflow-hidden" data-testid="about-boilerplate">
      <div className="flex items-start justify-between gap-4 mb-4">
        <h2 className="text-xl font-semibold text-gray-200">Reference Description</h2>
        <Button
          size="sm" onClick={handleCopy}
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs whitespace-nowrap"
          data-testid="boilerplate-copy-btn"
        >
          {copied ? <><CheckCircle2 className="w-3 h-3 mr-1.5" />Copied</> : <><Copy className="w-3 h-3 mr-1.5" />Copy both</>}
        </Button>
      </div>
      <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">One line</p>
      <p className="text-lg text-gray-300 mb-5">{ONE_LINE}</p>
      <p className="text-xs uppercase tracking-wider text-gray-500 mb-2">Three lines</p>
      <p className="text-lg text-gray-300">{THREE_LINE}</p>
    </div>
  );
};

const LinkedInSnippetCard = () => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    const ok = await copyToClipboard(LINKEDIN_POST);
    if (ok) {
      setCopied(true);
      toast.success('LinkedIn post copied \u2014 ready to paste');
      setTimeout(() => setCopied(false), 2500);
    } else {
      toast.error('Could not copy automatically \u2014 please select the text and copy manually');
    }
  };
  return (
    <div className="relative bg-gray-900/60 rounded-xl p-8 border border-gray-700 overflow-hidden" data-testid="about-linkedin-snippet">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-200">LinkedIn Post</h2>
          <p className="text-xs uppercase tracking-wider text-gray-500 mt-1">Ready to copy and paste</p>
        </div>
        <Button
          size="sm" onClick={handleCopy}
          className="bg-blue-600 hover:bg-blue-700 text-white text-xs whitespace-nowrap"
          data-testid="linkedin-snippet-copy-btn"
        >
          {copied ? <><CheckCircle2 className="w-3 h-3 mr-1.5" />Copied</> : <><Copy className="w-3 h-3 mr-1.5" />Copy post</>}
        </Button>
      </div>
      <pre className="text-lg text-gray-300 whitespace-pre-wrap font-sans leading-relaxed">{LINKEDIN_POST}</pre>
    </div>
  );
};

const AboutUsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-linear-to-br from-gray-900 via-gray-800 to-black text-white">
      <SEO
        title="About Us"
        description="MyInvestFolio is a private, independent system of wealth record for discerning investors — with built-in wealth continuity that quietly ensures the people you care about can find and understand your investments if you're ever unavailable. Learn about the founder, the philosophy, and how the platform is built."
        path="/about-us"
      />
      <header className="border-b border-gray-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-300 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
            <div className="flex items-center space-x-2">
              <img
                src="https://customer-assets.emergentagent.com/job_3876ffd2-0b67-4d0f-be27-32eb0667fc3a/artifacts/g551041i_MIF%20Logo%20White.png"
                alt="MyInvestFolio Logo"
                className="w-8 h-8 object-contain"
              />
              <span className="text-xl font-bold">
                My<span className="text-green-600">Invest</span><span className="text-blue-600">Folio</span>
              </span>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 max-w-4xl">
        <div className="space-y-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-emerald-600">About Us</h1>
            <div className="w-24 h-1 mx-auto bg-linear-to-r from-emerald-500 to-blue-500"></div>
          </div>

          <div className="space-y-8 text-gray-300 leading-relaxed">
            {ABOUT_SECTIONS.map(section => (
              <ContentSection
                key={section.title}
                title={section.title}
                paragraphs={section.paragraphs}
                footerLinkTo={section.footerLinkTo}
                footerLinkLabel={section.footerLinkLabel}
              />
            ))}

            {/* Founder bio — concise, factual, single paragraph */}
            <div className="relative bg-gray-800 rounded-xl p-8 border border-gray-700 overflow-hidden" data-testid="founder-bio">
              <span aria-hidden className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500" />
              <div className="flex items-start gap-4">
                <UserCircle2 className="w-10 h-10 text-emerald-400 shrink-0 mt-1" />
                <div>
                  <h2 className="text-xl font-semibold mb-3 text-emerald-400">About the Founder</h2>
                  <p className="text-lg mb-4">
                    Adejare R. OlaOluwa is the founder of MyInvestFolio. He had a distinguished career spanning
                    over three decades in Accounting, Banking, Consulting, Energy, and Investment Management,
                    across multiple reputable institutions, where he played multiple board-level roles.
                  </p>
                  <p className="text-lg mb-4">
                    In addition to professional qualifications in Engineering and Accounting, he attended many
                    International Executive programs at European Institute of Business Administration (INSEAD),
                    IESE Business School, London School of Economics, and Harvard Business School.
                  </p>
                  <p className="text-lg">
                    He founded MyInvestFolio to solve a problem he encountered first-hand: the absence of an
                    independent, private place for a discerning investor to see their complete portfolio across
                    institutions, jurisdictions and ownership structures. He writes on portfolio infrastructure
                    and economic development on LinkedIn.
                  </p>
                  <p className="text-lg mt-4">
                    Along the way he saw first-hand what happens when a lifetime&rsquo;s portfolio becomes a puzzle
                    for those left to understand it &mdash; which is why wealth continuity became a first-class
                    design principle of MyInvestFolio, not an afterthought.
                  </p>
                </div>
              </div>
            </div>

            {/* Abridged boilerplate — for any third party who needs a clean reference description */}
            <BoilerplateCard />

            {/* LinkedIn-ready post — for the founder to post directly */}
            <LinkedInSnippetCard />
          </div>

          <div className="text-center mt-16">
            <Button onClick={() => navigate('/')}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8 py-3 rounded-lg shadow-lg hover:shadow-xl transition-all">
              Return to Home
            </Button>
          </div>
        </div>
      </main>

      <footer className="border-t border-gray-700 mt-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center text-gray-400 text-sm">
            <p>&copy; 2026 MyInvestFolio. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutUsPage;
